import { useState, useEffect } from 'react';
import {
  getAdCreatorHistory,
  getAdCreatorSyncStatus,
  postAdCreatorFeedback,
  postAdCreatorSyncGoogleAds,
} from '../lib/adCreator';

interface GenerationRecord {
  id: number;
  generation_id: string;
  keywords: string;
  persona: string;
  scene?: string;
  created_at: string;
  asset_count: number;
  avg_quality: number;
  generation_time_seconds?: number;
}

interface PerformanceFeedback {
  id: number;
  asset_text: string;
  field_type: 'HEADLINE' | 'DESCRIPTION';
  impressions: number;
  clicks: number;
  ctr: number;
  performance_label: 'GOOD' | 'AVERAGE' | 'POOR' | 'LEARNING';
  feedback_date: string;
}

function parseKeywords(value: string | null | undefined): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    if (Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')) {
      return parsed;
    }
    return [];
  } catch {
    return [];
  }
}

export function CopyHistory() {
  const [records, setRecords] = useState<GenerationRecord[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<GenerationRecord | null>(null);
  const [feedbackData, setFeedbackData] = useState({
    impressions: '',
    clicks: '',
    conversions: '',
    costMicros: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    persona: '',
    limit: '20'
  });
  const [syncStatus, setSyncStatus] = useState<{
    lastSync: string | null;
    needsSync: boolean;
    syncing: boolean;
  }>({ lastSync: null, needsSync: false, syncing: false });

  useEffect(() => {
    fetchHistory();
    fetchSyncStatus();
  }, [filters]);

  async function fetchSyncStatus() {
    try {
      const data = await getAdCreatorSyncStatus();
      if (data.success) {
        setSyncStatus(prev => ({
          ...prev,
          lastSync: data.lastSync ?? null,
          needsSync: data.needsSync ?? false
        }));
      }
    } catch (err) {
      setError('取得同步狀態失敗');
    }
  }

  async function handleSync() {
    setSyncStatus(prev => ({ ...prev, syncing: true }));
    try {
      const data = await postAdCreatorSyncGoogleAds({});
      if (data.success && data.result) {
        alert(`同步完成！\n已同步 ${data.result.syncedAssets} 個素材\n匹配 ${data.result.matchedAssets} 個文案\n新增 ${data.result.newFeedbacks} 筆回饋`);
        fetchSyncStatus();
      } else {
        setError(data.error || '同步失敗');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '同步失敗');
    } finally {
      setSyncStatus(prev => ({ ...prev, syncing: false }));
    }
  }

  async function fetchHistory() {
    setLoading(true);
    try {
      const data = await getAdCreatorHistory<GenerationRecord>({
        persona: filters.persona || undefined,
        limit: filters.limit,
      });

      if (data.success) {
        setRecords(data.history ?? []);
      } else {
        setError(data.error || '獲取歷史記錄失敗');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '發生錯誤');
    } finally {
      setLoading(false);
    }
  }

  async function submitFeedback(assetId: number, assetText: string, fieldType: 'HEADLINE' | 'DESCRIPTION') {
    try {
      const data = await postAdCreatorFeedback({
        generationAssetId: assetId,
        adGroupId: selectedRecord?.generation_id || 'unknown',
        assetText,
        fieldType,
        impressions: parseInt(feedbackData.impressions) || 0,
        clicks: parseInt(feedbackData.clicks) || 0,
        conversions: parseFloat(feedbackData.conversions) || 0,
        costMicros: parseInt(feedbackData.costMicros) || 0,
        notes: feedbackData.notes
      });
      if (data.success) {
        alert('成效回饋提交成功！');
        setFeedbackData({ impressions: '', clicks: '', conversions: '', costMicros: '', notes: '' });
      } else {
        setError(data.error || '提交失敗');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '提交失敗');
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.875rem', fontWeight: 700, marginBottom: '0.5rem' }}>
          📚 文案生成歷史
        </h1>
        <p style={{ color: 'var(--color-text-secondary)' }}>
          查看過去生成的文案記錄，提交成效回饋數據
        </p>
      </div>

      {/* Filters */}
      <div style={{
        background: 'var(--color-surface)',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        display: 'flex',
        gap: '1rem',
        alignItems: 'center'
      }}>
        <div>
          <label style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
            Persona 篩選
          </label>
          <select
            value={filters.persona}
            onChange={(e) => setFilters({ ...filters, persona: e.target.value })}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: '1px solid var(--color-border)',
              fontSize: '0.875rem'
            }}
          >
            <option value="">全部</option>
            <option value="產後媽媽">產後媽媽</option>
            <option value="減肥瘦身">減肥瘦身</option>
            <option value="腰痠背痛">腰痠背痛</option>
            <option value="怕熱女孩">怕熱女孩</option>
            <option value="名媛貴婦">名媛貴婦</option>
          </select>
        </div>

        <div>
          <label style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
            顯示數量
          </label>
          <select
            value={filters.limit}
            onChange={(e) => setFilters({ ...filters, limit: e.target.value })}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: '1px solid var(--color-border)',
              fontSize: '0.875rem'
            }}
          >
            <option value="10">10 筆</option>
            <option value="20">20 筆</option>
            <option value="50">50 筆</option>
          </select>
        </div>

        <button
          onClick={fetchHistory}
          style={{
            marginLeft: 'auto',
            padding: '0.5rem 1.5rem',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: '0.375rem',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          🔄 重新載入
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {syncStatus.lastSync && (
            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>
              上次同步: {new Date(syncStatus.lastSync).toLocaleString('zh-TW')}
            </span>
          )}
          <button
            onClick={handleSync}
            disabled={syncStatus.syncing}
            style={{
              padding: '0.5rem 1.5rem',
              background: syncStatus.syncing ? 'var(--color-border)' : 'var(--color-success)',
              color: 'white',
              borderRadius: '0.375rem',
              border: 'none',
              cursor: syncStatus.syncing ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {syncStatus.syncing ? '⏳ 同步中...' : '📥 同步 Google Ads'}
            {syncStatus.needsSync && !syncStatus.syncing && (
              <span style={{
                width: '8px',
                height: '8px',
                background: '#ef4444',
                borderRadius: '50%',
                display: 'inline-block'
              }} />
            )}
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          background: 'var(--color-error-bg)',
          color: 'var(--color-error)',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          ❌ {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-secondary)' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          載入中...
        </div>
      ) : (
        <div style={{
          background: 'var(--color-surface)',
          borderRadius: '0.75rem',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600 }}>時間</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600 }}>關鍵字</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600 }}>Persona</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: 600 }}>文案數</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: 600 }}>平均品質</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: 600 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    {formatDate(record.created_at)}
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    {parseKeywords(record.keywords).slice(0, 3).join(', ')}
                    {parseKeywords(record.keywords).length > 3 && '...'}
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      background: 'var(--color-primary-light)',
                      borderRadius: '9999px',
                      fontSize: '0.75rem'
                    }}>
                      {record.persona}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', textAlign: 'center' }}>
                    {record.asset_count}
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', textAlign: 'center' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.25rem',
                      background: (record.avg_quality || 0) >= 70 ? '#dcfce7' : (record.avg_quality || 0) >= 50 ? '#fef9c3' : '#fee2e2',
                      color: (record.avg_quality || 0) >= 70 ? '#166534' : (record.avg_quality || 0) >= 50 ? '#854d0e' : '#991b1b',
                      fontWeight: 600
                    }}>
                      {Math.round(record.avg_quality || 0)}分
                    </span>
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', textAlign: 'center' }}>
                    <button
                      onClick={() => setSelectedRecord(record)}
                      style={{
                        padding: '0.375rem 0.75rem',
                        background: 'var(--color-primary)',
                        color: 'white',
                        borderRadius: '0.25rem',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                    >
                      回饋成效
                    </button>
                  </td>
                </tr>
              ))}
              {records.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                    暫無歷史記錄
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Feedback Modal */}
      {selectedRecord && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setSelectedRecord(null)}
        >
          <div
            style={{
              background: 'var(--color-surface)',
              borderRadius: '0.75rem',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem' }}>
              📊 提交成效回饋
            </h3>

            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--color-bg)', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>
                生成批次
              </div>
              <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                {selectedRecord.generation_id}
              </div>
            </div>

            <div style={{ display: 'grid', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                  曝光次數 (Impressions)
                </label>
                <input
                  type="number"
                  value={feedbackData.impressions}
                  onChange={(e) => setFeedbackData({ ...feedbackData, impressions: e.target.value })}
                  placeholder="例如: 10000"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--color-border)'
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                  點擊次數 (Clicks)
                </label>
                <input
                  type="number"
                  value={feedbackData.clicks}
                  onChange={(e) => setFeedbackData({ ...feedbackData, clicks: e.target.value })}
                  placeholder="例如: 500"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--color-border)'
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                  轉換數 (Conversions)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={feedbackData.conversions}
                  onChange={(e) => setFeedbackData({ ...feedbackData, conversions: e.target.value })}
                  placeholder="例如: 10"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--color-border)'
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                  花費 (Cost in micros)
                </label>
                <input
                  type="number"
                  value={feedbackData.costMicros}
                  onChange={(e) => setFeedbackData({ ...feedbackData, costMicros: e.target.value })}
                  placeholder="例如: 1000000 (代表 $1)"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--color-border)'
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                  備註
                </label>
                <textarea
                  value={feedbackData.notes}
                  onChange={(e) => setFeedbackData({ ...feedbackData, notes: e.target.value })}
                  placeholder="選填：補充說明..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--color-border)',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => setSelectedRecord(null)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'transparent',
                  border: '1px solid var(--color-border)',
                  borderRadius: '0.5rem',
                  cursor: 'pointer'
                }}
              >
                取消
              </button>
              <button
                onClick={() => {
                  // For demo, we'll use placeholder asset info
                  submitFeedback(1, '示例文案', 'HEADLINE');
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'var(--color-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                提交回饋
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
